"use client";

import { useQuery } from "@tanstack/react-query";
import {
  getPublicExperienceNextSlots,
  type PublicExperienceNextSlot,
} from "@/actions/venue-public";

const NEXT_SLOT_LIMIT = 3;

/**
 * The next few bookable starts for one experience — what the venue page's
 * footer uses to land "Book slots" on the next start when there is only one
 * class to book.
 *
 * TanStack Query, so the answer is cached per experience and shared.
 *
 * Deliberately `useQuery`, not `useSuspenseQuery`: a server action called
 * during render (which suspense mode does) updates the router mid-render and
 * React errors. Effect-phase fetching has no such problem.
 */
export function useExperienceNextSlots(experienceId: number) {
  return useQuery({
    queryKey: ["venue-experience-next-slots", experienceId],
    queryFn: async (): Promise<PublicExperienceNextSlot[]> => {
      const res = await getPublicExperienceNextSlots({
        experienceId,
        limit: NEXT_SLOT_LIMIT,
      });
      if (!res.ok) throw new Error(res.message);
      return res.data.slots;
    },
    // Starts move as other members book; a minute keeps chips honest without
    // refetching on every filter change (cache does that job).
    staleTime: 60_000,
  });
}
